import { QueryClient } from 'react-query';

/**
 * The app's one react-query client. The new StrengthX's data (keys starting
 * 'sx') is kept fresh for 30 seconds, so opening a page again or switching
 * back to the tab shows what is already there instead of asking the API
 * again; every change it makes still refreshes the lists it affects. Chat,
 * the alerts, the bell and the Today triage poll on their own, and chat, the
 * alerts and the bell also refresh when the tab comes back.
 *
 * Everything else (the current portal) counts as fresh for 10 seconds: two
 * parts of a page asking for the same thing as it opens share one request
 * instead of each fetching it, and coming back to the tab within those 10
 * seconds doesn't refetch every list. It still refreshes on focus after that.
 */
export const SX_STALE_TIME = 30_000;
export const DEFAULT_STALE_TIME = 10_000;

/**
 * Reference data that changes only when someone saves it (the coaches, coaching
 * rules, the Playbook, templates, preferences, alert settings): fresh for 10
 * minutes and kept in memory as long, so moving between pages doesn't ask for
 * it again. Every save of it refreshes it straight away (invalidateQueries or
 * setQueryData where it is saved), and a reload refreshes it too.
 */
export const REFERENCE_STALE_TIME = 10 * 60_000;
export const REFERENCE_CACHE_TIME = 10 * 60_000;

/** The reference data's keys (the start of each). */
export const REFERENCE_KEYS = [
  ['sx', 'coaches'],
  ['sx', 'coaching-rules'],
  ['sx', 'alertSettings'],
  ['sx', 'templates'],
  ['cueLibrary'],
  ['userPreferences'],
];

/** New-portal keys that don't start with 'sx' but follow its policy. */
const SX_LIKE_KEYS = ['program-settings', 'program-defaults', 'day-places', 'generator-options', 'client-emails', 'client-broadcasts', 'askHome', 'phaseEquipment'];

const SX = { staleTime: SX_STALE_TIME, refetchOnWindowFocus: false };

export const createQueryClient = (): QueryClient => {
  const client = new QueryClient({ defaultOptions: { queries: { staleTime: DEFAULT_STALE_TIME } } });
  // The first match wins, so the specific keys come before 'sx' and repeat what they keep from it.
  // The two shared with the current portal (Playbook, preferences) keep its refetch on focus.
  REFERENCE_KEYS.forEach((key) => client.setQueryDefaults(key, {
    ...(key[0] === 'sx' ? SX : {}),
    staleTime: REFERENCE_STALE_TIME,
    cacheTime: REFERENCE_CACHE_TIME,
  }));
  client.setQueryDefaults(['sx', 'chat'], { staleTime: 10_000, refetchOnWindowFocus: true });
  client.setQueryDefaults(['sx', 'alerts'], { staleTime: SX_STALE_TIME, refetchOnWindowFocus: true });
  client.setQueryDefaults(['sx'], SX);
  client.setQueryDefaults(['sxNotifications'], { staleTime: SX_STALE_TIME, refetchOnWindowFocus: true });
  SX_LIKE_KEYS.forEach((key) => client.setQueryDefaults([key], SX));
  return client;
};
